import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { ModalState, MovementTypeForm, MovementType } from '../types/system-parameters.types';

interface MovementTypeModalProps {
  modalState: ModalState;
  onClose: () => void;
  onSave: (formData: MovementTypeForm, isEdit?: boolean, itemId?: string) => void;
}

export const MovementTypeModal: React.FC<MovementTypeModalProps> = ({
  modalState,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<MovementTypeForm>({
    code: '',
    name: '',
    description: '',
    direction: 'entry',
    requiresApproval: false,
    category: '',
    orderIndex: 1
  });
  const [loading, setLoading] = useState(false);

  // Resetear formulario cuando se abra en modo crear o cuando cambie el item
  useEffect(() => {
    if (modalState.isOpen) {
      if (modalState.mode === 'edit' && modalState.item) {
        const movementType = modalState.item as MovementType;
        setFormData({
          code: movementType.additionalData.code,
          name: movementType.additionalData.name,
          description: movementType.additionalData.description,
          direction: movementType.additionalData.direction,
          requiresApproval: movementType.additionalData.requiresApproval,
          category: movementType.additionalData.category,
          orderIndex: movementType.additionalData.orderIndex
        });
      } else {
        setFormData({
          code: '',
          name: '',
          description: '',
          direction: 'entry',
          requiresApproval: false,
          category: '',
          orderIndex: 1
        });
      }
    }
  }, [modalState.isOpen, modalState.mode, modalState.item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code.trim() || !formData.name.trim() || !formData.category.trim()) {
      return;
    }

    setLoading(true);
    try {
      // Simular delay de red
      await new Promise(resolve => setTimeout(resolve, 800));

      onSave(
        formData,
        modalState.mode === 'edit',
        modalState.item?.id
      );
      onClose();
    } catch (error) {
      console.error('Error al guardar tipo de movimiento:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeGeneration = () => {
    if (formData.name) {
      const code = formData.name
        .toUpperCase()
        .replace(/[^A-Z0-9\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 20);
      setFormData(prev => ({ ...prev, code }));
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'edit' ? 'Editar Tipo de Movimiento' : 'Nuevo Tipo de Movimiento'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
              }}
              onBlur={handleCodeGeneration}
              placeholder="Ej: Ingreso por Venta"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="code">Código *</Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              placeholder="Ej: INGRESO_VENTA"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Categoría *</Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value.toUpperCase() }))}
              placeholder="Ej: VENTAS"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="direction">Dirección *</Label>
            <Select value={formData.direction} onValueChange={(value: 'entry' | 'exit') => setFormData(prev => ({ ...prev, direction: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar dirección" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="entry">Entrada</SelectItem>
                <SelectItem value="exit">Salida</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="orderIndex">Orden</Label>
            <Input
              id="orderIndex"
              type="number"
              min="1"
              value={formData.orderIndex}
              onChange={(e) => setFormData(prev => ({ ...prev, orderIndex: parseInt(e.target.value) || 1 }))}
              placeholder="1"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="requiresApproval"
              checked={formData.requiresApproval}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requiresApproval: checked }))}
            />
            <Label htmlFor="requiresApproval">Requiere aprobación</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripción del tipo de movimiento"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!formData.code.trim() || !formData.name.trim() || !formData.category.trim() || loading}
              className="bg-macartur-primary"
            >
              {loading ? 'Guardando...' : modalState.mode === 'edit' ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};